/**
 * SpriteUIConfig — NineSlice dimension registry for PixelLab sprite UI assets.
 * Pure data, no scene dependencies. Tunable independently.
 *
 * Each config defines the inset distances from each edge that form the
 * non-scaling corners/borders for Phaser's native NineSlice.
 *
 * Assets are generated at base size then upscaled 1.4× with nearest-neighbor.
 */

export interface NineSliceConfig {
  texture: string;
  leftWidth: number;
  rightWidth: number;
  topHeight: number;
  bottomHeight: number;
  /** Native pixel width of the source image */
  nativeW: number;
  /** Native pixel height of the source image */
  nativeH: number;
}

// ── NineSlice-capable assets (stretchable panels, buttons, bars) ──

export const UI_NINE_SLICE: Record<string, NineSliceConfig> = {
  // Panels — thick riveted borders with orange corner brackets
  panel_large: {
    texture: 'ui_panel_large',
    leftWidth: 39, rightWidth: 39, topHeight: 34, bottomHeight: 34,
    nativeW: 280, nativeH: 224,
  },
  panel_small: {
    texture: 'ui_panel_small',
    leftWidth: 28, rightWidth: 28, topHeight: 25, bottomHeight: 25,
    nativeW: 179, nativeH: 134,
  },
  panel_wave: {
    texture: 'ui_panel_wave',
    leftWidth: 34, rightWidth: 34, topHeight: 20, bottomHeight: 20,
    nativeW: 252, nativeH: 112,
  },
  panel_upgrade: {
    texture: 'ui_panel_upgrade',
    leftWidth: 31, rightWidth: 31, topHeight: 45, bottomHeight: 28,
    nativeW: 224, nativeH: 179,
  },
  panel_frame: {
    texture: 'ui_panel_frame',
    leftWidth: 34, rightWidth: 34, topHeight: 31, bottomHeight: 31,
    nativeW: 280, nativeH: 224,
  },

  // Buttons — thinner metallic border
  button_wide: {
    texture: 'ui_button_wide',
    leftWidth: 17, rightWidth: 17, topHeight: 11, bottomHeight: 11,
    nativeW: 179, nativeH: 50,
  },
  button_normal: {
    texture: 'ui_button_normal',
    leftWidth: 17, rightWidth: 17, topHeight: 14, bottomHeight: 14,
    nativeW: 179, nativeH: 56,
  },

  // Bars — narrow top/bottom, wider left/right caps
  bar_hp: {
    texture: 'ui_bar_hp',
    leftWidth: 22, rightWidth: 22, topHeight: 8, bottomHeight: 8,
    nativeW: 224, nativeH: 39,
  },
  bar_currency: {
    texture: 'ui_bar_currency',
    leftWidth: 22, rightWidth: 22, topHeight: 11, bottomHeight: 11,
    nativeW: 224, nativeH: 50,
  },
  bar_frame: {
    texture: 'ui_bar_frame',
    leftWidth: 20, rightWidth: 20, topHeight: 8, bottomHeight: 8,
    nativeW: 224, nativeH: 45,
  },

  // Frame — symmetric thick border
  frame_square: {
    texture: 'ui_frame_square',
    leftWidth: 22, rightWidth: 22, topHeight: 22, bottomHeight: 22,
    nativeW: 134, nativeH: 134,
  },
};

// ── Fixed-size assets (used as-is or with setDisplaySize, not NineSlice) ──

export const UI_FIXED = {
  button_speed:    'ui_button_speed',      // 67×67
  button_square:   'ui_button_square',     // 67×67
  slot_component:  'ui_slot_component',    // 90×112
  slot_locked:     'ui_slot_locked',       // 90×112
  banner_title:    'ui_banner_title',      // 280×90
  banner_victory:  'ui_banner_victory',    // 280×90
  banner_defeat:   'ui_banner_defeat',     // 280×90
  icon_frame:      'ui_icon_frame',        // 67×67
} as const;

/** All texture keys that must be loaded in BootScene. */
export const ALL_UI_TEXTURES: { key: string; path: string }[] = [
  // NineSlice assets
  ...Object.values(UI_NINE_SLICE).map(c => ({ key: c.texture, path: `ui-experiments/${c.texture.replace('ui_', '')}.png` })),
  // Fixed assets
  { key: 'ui_button_speed',     path: 'ui-experiments/button_speed.png' },
  { key: 'ui_button_square',    path: 'ui-experiments/button_square.png' },
  { key: 'ui_slot_component',   path: 'ui-experiments/slot_component.png' },
  { key: 'ui_slot_locked',      path: 'ui-experiments/slot_locked.png' },
  { key: 'ui_banner_title',     path: 'ui-experiments/title_banner.png' },
  { key: 'ui_banner_victory',   path: 'ui-experiments/banner_victory.png' },
  { key: 'ui_banner_defeat',    path: 'ui-experiments/banner_defeat.png' },
  { key: 'ui_icon_frame',       path: 'ui-experiments/icon_frame.png' },
];
